import { makeCygnusApiCall } from "../../../../../api/api";
import { fetchFirmClients } from "../../../../client_onboarding/ClientsGrid/ClientsGrid.service";

/**
 * Fetches clients based on a search token input.
 *
 * @param inputValue - The search string used to query clients.
 * @param setClientSearchAPIProgress - Function to toggle the loading state.
 * @returns A promise resolving to an array of matching client records, or an empty array if input is empty or request fails.
 */
export const fetchClientsUsingToken = async (
  inputValue: string,
  setClientSearchAPIProgress: (arg: boolean) => void
) => {
  setClientSearchAPIProgress(true);

  // Don't proceed with API call if no input is provided
  if (!inputValue) {
    setClientSearchAPIProgress(false);
    return [];
  }

  const payload = {
    search_token: inputValue,
  };

  try {
    const response = await makeCygnusApiCall({
      type: "POST",
      url: "/mdm/forms/fetch_clients_by_token/",
      data: payload,
      show_message: true,
      customized_success_message: "Fetched clients successfully.",
      customized_failure_message:
        "Unable to fetch client data, please try again.",
      customized_error_message:
        "Error occurred while fetching clients. Please contact support.",
    });

    if (response.success) {
      return response.data;
    }
  } catch (err) {
    console.error("fetchOptions error:", err);
  } finally {
    setClientSearchAPIProgress(false);
  }

  return [];
};

/**
 * Fetches a paginated list of all clients from the backend and updates shared state.
 *
 * @async
 * @function fetchAllClients
 * @param setSharedData Function to update shared state with the fetched client records
 * @param searchText Optional search string to filter clients by name or criteria
 * @param setRefreshState Optional function to toggle loading/refresh state (true when fetching, false when complete)
 *
 * - Sends a request to `fetchFirmClients` with offset=0, limit=10, and sourceCode="all".
 * - Updates `allClientsData` in shared state with the returned client records.
 * - Manages refresh/loading state if `setRefreshState` is provided.
 * - Logs an error if the API call fails.
 *
 * @returns {Promise<void>} Resolves when the fetch and state updates are complete
 */
export const fetchAllClients = async (
  setSharedData: (data: any) => void,
  searchText?: string,
  setRefreshState?: (data: boolean) => void,
) => {
  try {
    setRefreshState?.(true);
    const response = await fetchFirmClients({
      offset: 0,
      limit: 10,
      searchText: searchText ?? "",
      sourceCode: "all",
      filter: {
        Status: "Approved",
      },
    });

    setSharedData((prev: any) => ({
      ...prev,
      allClientsData: response.records,
    }));
    setRefreshState?.(false);
  } catch (err) {
    console.error("Error fetching clients:", err);
  }
};